import { createClient } from '@supabase/supabase-js'
import { FETCH_TIMEOUT_MS } from './constants.js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

export const authEnabled = Boolean(supabaseUrl && supabaseAnonKey)
export const supabase = authEnabled ? createClient(supabaseUrl as string, supabaseAnonKey as string) : undefined

// A short-lived data client tied to one captured login. It has no auth session
// storage or refresh listener; account switching cannot redirect its requests.
export function createSessionDataClient(accessToken: string, signal: AbortSignal) {
  if (!supabaseUrl || !supabaseAnonKey || !accessToken) throw new Error('supabase_unavailable')
  return createClient(supabaseUrl, supabaseAnonKey, {
    accessToken: async () => accessToken,
    global: { fetch: (url, init) => fetch(url, { ...init, signal }) }
  })
}

function getSupabaseBrowserStorageKeys(): string[] {
  if (!supabaseUrl) return []
  try {
    const baseUrl = new URL(supabaseUrl)
    const storageKey = `sb-${baseUrl.hostname.split('.')[0]}-auth-token`
    return [storageKey, `${storageKey}-code-verifier`, `${storageKey}-user`]
  } catch {
    return []
  }
}

export async function clearPersistedBrowserAuthState(): Promise<void> {
  try {
    // Race against FETCH_TIMEOUT_MS: signOut({ scope: 'local' }) acquires a
    // cross-tab Web Lock internally, so a second tab holding the leader lock
    // would cause an indefinite hang. The manual key removal below is the
    // authoritative local wipe either way.
    await Promise.race([
      supabase?.auth.signOut({ scope: 'local' }) ?? Promise.resolve(),
      new Promise<void>((_, reject) =>
        setTimeout(() => reject(new Error('signOut lock timeout')), FETCH_TIMEOUT_MS)
      ),
    ])
  } catch {
    // SDK cleanup races or cross-tab lock contention — manual wipe is authoritative.
  }

  if (typeof window === 'undefined') return
  const keys = getSupabaseBrowserStorageKeys()
  for (const key of keys) {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // Ignore storage capability failures.
    }
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      // Ignore storage capability failures.
    }
  }
}

export async function settleStartupAuthCodeSession(): Promise<void> {
  if (!supabase) return
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  if (!code) return

  try {
    // Startup compatibility seam: Supabase validates the short-lived code before
    // app.ts boots and takes back ownership of session/profile truth.
    await supabase.auth.exchangeCodeForSession(code)
  } finally {
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    url.searchParams.delete('error')
    url.searchParams.delete('error_description')
    window.history.replaceState({}, '', url.toString())
  }
}
